import { DateService, PulseEnvironment, PulseWindow } from '@pulse/browser-shared';
import { AsyncTask } from '@pulse/common';
import { StreamStats, measureStreamSizeIfConsumed, valueSize } from '../../sizeof';

export type WrappedRequest = {
    wrappedRequest: Request;
    bodyStats: Promise<StreamStats | undefined>;
};

function isStream(v: any, win: PulseWindow): v is ReadableStream {
    return 'ReadableStream' in win && v instanceof win.ReadableStream;
}

export function wrapRequest(
    info: RequestInfo,
    init: RequestInit | undefined,
    date: DateService,
    env: PulseEnvironment,
): WrappedRequest {
    if (init?.body && !isStream(init.body, env.win)) {
        const task = new AsyncTask<StreamStats | undefined>();

        try {
            const size = valueSize(init.body, env.win);
            task.resolve({ size, start: -1, end: -1, cancelled: false });
        } catch (ex) {
            task.reject(ex);
        }

        const wrappedRequest = new Request(info, init);

        return {
            wrappedRequest,
            bodyStats: task.waitUntilResolved(),
        };
    }

    if (typeof info === 'object' && info.body instanceof ReadableStream) {
        const { wrappedStream, complete } = measureStreamSizeIfConsumed(info.body, date, env.win);
        const wrappedInit: RequestInit = {
            cache: info?.cache,
            credentials: info?.credentials,
            headers: info?.headers,
            integrity: info?.integrity,
            keepalive: info?.keepalive,
            method: info?.method,
            mode: info?.mode,
            redirect: info?.redirect,
            referrer: info?.referrer,
            referrerPolicy: info?.referrerPolicy,
            signal: info?.signal,
            ...(init || {}),
            body: wrappedStream,
        };

        const wrappedRequest = new Request(info, wrappedInit);

        return { wrappedRequest, bodyStats: complete };
    }

    const wrappedRequest = new Request(info, init);

    return { wrappedRequest, bodyStats: Promise.resolve(undefined) };
}
